/** @jsxImportSource theme-ui @jsxRuntime classic */
import { FixedSizeList } from 'react-window';
import * as Icons from '@tishman/icons';
import { VisuallyHidden } from '@reach/visually-hidden';
import React from 'react';

import {
  Container,
  Box,
  useFadeAnimationTrail,
  Flex,
  Button,
  useBreakpointIndex,
} from '..';

import {
  SocialCarouselCard,
  SocialCarouselCardProps,
} from './SocialCarouselCard';
import { useSocialCarousel } from './useSocialCarousel';

export interface SocialCarouselProps {
  cards: SocialCarouselCardProps[];
  /**
   * A callback for handling a change in active card id.
   *
   * The most likely reason for this to change is
   * when a card has been clicked.
   */
  onActiveCardChange?: (id: string | null) => void;
  desktopCardsPaddingLeft?: string;
  mobileContainerFullWidth?: boolean;
  carouselVariant?: 'default' | 'private-events';
}
/**
 * `SocialCarousel` will render a list of `cards` in a `CarouselList`
 *  and a `CarouselNavigation`.
 *
 * @see https://tishman.netlify.app/components/carousel
 */

interface RowProps {
  index: number;
  style: object;
}

const Row = ({
  index,
  style,
  cards,
  onActiveCardChange,
  carouselVariant,
}: RowProps & {
  cards: SocialCarouselCardProps[];
  onActiveCardChange?: (id: string | null) => void;
  carouselVariant?: 'default' | 'private-events';
}) => {
  const card = cards[index];
  return (
    <div key={index} style={{ ...style }}>
      <SocialCarouselCard
        key={card.caption}
        {...card}
        carouselVariant={carouselVariant}
        onClick={() => onActiveCardChange?.(card.id)}
      />
    </div>
  );
};

export function SocialCarousel({
  cards,
  onActiveCardChange,
  desktopCardsPaddingLeft = '300px',
  mobileContainerFullWidth = false,
  carouselVariant,
}: SocialCarouselProps): React.JSX.Element {
  const [, ref] = useFadeAnimationTrail({
    numberOfItems: cards.length,
    threshold: 0.4,
  });

  const isMobile = useBreakpointIndex() < 2;

  const {
    beginning,
    end,
    handleNext,
    handlePrevious,
    listRef,
    onListScroll,
    desktopContainerPaddingLeft,
    cardWidth,
  } = useSocialCarousel(cards);

  return (
    <Box data-module="SocialCarousel" ref={ref} sx={{ position: 'relative' }}>
      <Flex sx={{ flexDirection: isMobile ? 'column-reverse' : 'column' }}>
        {/* Scroll Arrow Buttons */}
        <Container
          sx={{
            alignSelf: 'center',
            display: 'flex',
            // right align mobile & tablet, left align desktop
            justifyContent: ['flex-end', null, 'flex-start'],
            mb: 4,
          }}
        >
          <Flex sx={{ display: 'flex' }}>
            <Button
              data-element="CarouselNavigationPrevious"
              disabled={beginning}
              onClick={handlePrevious}
              sx={{
                'cursor': 'pointer',
                ':not(:disabled):hover svg': {
                  transform: `translateX(-5px) scale(${
                    24 / 18
                  }) rotate(180deg)`,
                },
              }}
              tabIndex={0}
              variant="icon"
            >
              <VisuallyHidden>Previous Item</VisuallyHidden>
              <Icons.Arrow
                aria-hidden
                style={{
                  transition: 'transform 0.3s ease-in-out',
                  transform: `scale(${24 / 18}) rotate(180deg)`,
                }}
              />
            </Button>
            <Button
              data-element="CarouselNavigationNext"
              disabled={end}
              onClick={handleNext}
              sx={{
                'cursor': 'pointer',
                ':not(:disabled):hover svg': {
                  transform: `translateX(5px) scale(${24 / 18}) `,
                },
              }}
              tabIndex={0}
              variant="icon"
            >
              <VisuallyHidden>Next Item</VisuallyHidden>
              <Icons.Arrow
                aria-hidden
                style={{
                  transition: 'transform 0.3s ease-in-out',
                  transform: `scale(${24 / 18})`,
                }}
              />
            </Button>
          </Flex>
        </Container>
        {/* Scrolling List */}
        <Box
          sx={{
            paddingLeft: beginning
              ? ['10px', desktopCardsPaddingLeft]
              : [0, desktopContainerPaddingLeft - cardWidth],
            overflow: 'hidden',
          }}
        >
          <FixedSizeList
            height={isMobile ? 250 : 450}
            itemCount={cards.length}
            itemSize={cardWidth}
            layout="horizontal"
            onScroll={onListScroll}
            ref={listRef as React.LegacyRef<any>} // eslint-disable-line @typescript-eslint/no-explicit-any -- this won't work without the any type here
            style={{
              scrollbarWidth: 'none',
              marginTop: isMobile ? 4 : 0,
            }}
            width={
              isMobile
                ? mobileContainerFullWidth && typeof window !== 'undefined'
                  ? window.innerWidth
                  : 375
                : Math.min(1725, window.innerWidth - 24)
            }
          >
            {({ index, style }) => (
              <Row
                cards={cards}
                carouselVariant={carouselVariant}
                index={index}
                onActiveCardChange={onActiveCardChange}
                style={style}
              />
            )}
          </FixedSizeList>
        </Box>
      </Flex>
    </Box>
  );
}
